import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdReceipt,
  MdAccountBalance,
  MdPercent,
  MdCurrencyRupee,
  MdBusiness,
  MdPictureAsPdf,
  MdErrorOutline,
  MdCancel,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import { indianStates } from '../../Utils/states';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/invoices';

const INVOICE_STATUS = {
  issued: { label: 'Issued', bg: '#dcfce7', fg: '#166534' },
  cancelled: { label: 'Cancelled', bg: '#fee2e2', fg: '#991b1b' },
  returned: { label: 'Returned · Credit note due', bg: '#ffedd5', fg: '#9a3412' },
};

const PAYMENT_STATUS_STYLES = {
  paid: { bg: '#dcfce7', fg: '#166534' },
  pending: { bg: '#fef3c7', fg: '#92400e' },
  processing: { bg: '#e0f2fe', fg: '#075985' },
  failed: { bg: '#fee2e2', fg: '#991b1b' },
};

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'amount_desc', label: 'Invoice total: high to low' },
  { value: 'amount_asc', label: 'Invoice total: low to high' },
];

const COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice No.' },
  { key: 'orderNumber', label: 'Order No.' },
  { key: 'customer', label: 'Customer' },
  { key: 'customerType', label: 'Type', center: true },
  { key: 'gstin', label: 'GSTIN' },
  { key: 'invoiceTotal', label: 'Invoice Total', numeric: true, money: true },
  { key: 'payment', label: 'Payment' },
  { key: 'invoiceStatus', label: 'Status' },
  { key: 'pdf', label: 'PDF', center: true },
];

const paymentModeLabel = (m) => (!m || m === '-' ? '-' : m === 'cod' ? 'COD' : m === 'upi' ? 'UPI' : m.charAt(0).toUpperCase() + m.slice(1));
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 12 });

const InvoiceReport = () => {
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [customerType, setCustomerType] = useState('');
  const [state, setState] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('');
  const [pdf, setPdf] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { sortBy };
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    if (customerType) p.customerType = customerType;
    if (state) p.state = state;
    if (paymentStatus) p.paymentStatus = paymentStatus;
    if (invoiceStatus) p.invoiceStatus = invoiceStatus;
    if (pdf) p.pdf = pdf;
    return p;
  }, [from, to, search, customerType, state, paymentStatus, invoiceStatus, pdf, sortBy]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const setFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };

  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };

  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };

  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setCustomerType('');
    setState('');
    setPaymentStatus('');
    setInvoiceStatus('');
    setPdf('');
    setSortBy('date_desc');
    setPage(0);
  };

  const exportObjects = (rows) =>
    rows.map((r) => ({
      'Invoice No.': r.invoiceNumber,
      'Invoice Date': fmtDate(r.invoiceDate),
      'Order No.': r.orderNumber,
      Customer: r.customer,
      Phone: r.phone,
      Type: r.customerType,
      GSTIN: r.gstin,
      'Place of Supply': r.placeOfSupply,
      'Taxable Value': r.taxableValue,
      CGST: r.cgst,
      SGST: r.sgst,
      IGST: r.igst,
      'Total GST': r.totalGst,
      'Delivery Charge': r.deliveryCharge,
      'Round-off': r.roundOff,
      'Invoice Total': r.invoiceTotal,
      'Payment Method': paymentModeLabel(r.paymentMode),
      'Payment Status': r.paymentStatus,
      Status: INVOICE_STATUS[r.invoiceStatus]?.label || r.invoiceStatus,
      'Invoice PDF': r.pdfUrl || 'Missing',
    }));

  const fileStamp = () => `Invoice_Report_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), 'Invoices');
      else {
        const printCols = ['invoiceNumber', 'orderNumber', 'customer', 'customerType', 'gstin', 'invoiceTotal', 'invoiceStatus'];
        const cols = COLUMNS.filter((c) => printCols.includes(c.key));
        openPrintView({
          title: 'Invoice Report',
          subtitle: `${rangeLabel(from, to)} · ${rows.length} invoices`,
          headers: cols.map((c) => c.label),
          cells: rows.map((r) =>
            cols.map((c) => {
              if (c.key === 'invoiceStatus') return { value: INVOICE_STATUS[r.invoiceStatus]?.label, className: r.invoiceStatus === 'issued' ? '' : 'warn' };
              if (c.money) return { value: inr(r[c.key]), className: 'num' };
              return { value: r[c.key] || '-' };
            })
          ),
        });
      }
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.rows || [];

  const cards = [
    { label: 'Invoices', value: (stats.totalInvoices || 0).toLocaleString('en-IN'), icon: MdReceipt, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${stats.issued || 0} issued · ${stats.cancelled || 0} cancelled · ${stats.returned || 0} returned` },
    { label: 'Taxable Value', value: inr(stats.taxableValue), icon: MdAccountBalance, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: 'Issued invoices' },
    { label: 'Total GST', value: inr(stats.totalGst), icon: MdPercent, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: `CGST ${inr(stats.cgst)} · SGST ${inr(stats.sgst)} · IGST ${inr(stats.igst)}` },
    { label: 'Invoice Value', value: inr(stats.invoiceValue), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: 'Issued invoices incl. GST' },
    { label: 'B2B / B2C', value: `${stats.b2b || 0} / ${stats.b2c || 0}`, icon: MdBusiness, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: 'Business vs consumer' },
    { label: 'Missing PDFs', value: stats.missingPdf || 0, icon: MdErrorOutline, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: 'Click to show them', onClick: () => { setPdf((p) => (p === 'missing' ? '' : 'missing')); setPage(0); }, active: pdf === 'missing' },
  ];

  const activeFilterCount = [from || to, search, customerType, state, paymentStatus, invoiceStatus, pdf, sortBy !== 'date_desc'].filter(Boolean).length;

  const renderCell = (c, r) => {
    switch (c.key) {
      case 'customer':
        return (
          <Box sx={{ lineHeight: 1.25 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: FONT }}>{r.customer}</Typography>
            {r.phone && <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{r.phone}</Typography>}
          </Box>
        );
      case 'customerType':
        return <Chip size="small" label={r.customerType} sx={r.customerType === 'B2B' ? pill('#ede9fe', '#5b21b6') : pill('#e0f2fe', '#075985')} />;
      case 'gstin':
        return r.gstin ? <Typography sx={{ fontSize: 12.5, fontFamily: 'monospace', fontWeight: 600 }}>{r.gstin}</Typography> : <Typography sx={{ color: '#94a3b8' }}>—</Typography>;
      case 'payment': {
        const ps = PAYMENT_STATUS_STYLES[r.paymentStatus] || { bg: '#f1f5f9', fg: '#475569' };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: FONT, minWidth: 34 }}>{paymentModeLabel(r.paymentMode)}</Typography>
            <Chip size="small" label={r.paymentStatus || '-'} sx={{ ...pill(ps.bg, ps.fg), textTransform: 'capitalize' }} />
          </Box>
        );
      }
      case 'invoiceStatus': {
        const s = INVOICE_STATUS[r.invoiceStatus] || INVOICE_STATUS.issued;
        return <Chip size="small" label={s.label} sx={pill(s.bg, s.fg)} />;
      }
      case 'pdf':
        return r.pdfUrl ? (
          <Tooltip title="Open invoice PDF" arrow>
            <IconButton size="small" component="a" href={r.pdfUrl} target="_blank" rel="noopener noreferrer" sx={{ color: '#dc2626' }}>
              <MdPictureAsPdf size={20} />
            </IconButton>
          </Tooltip>
        ) : (
          <Chip size="small" icon={<MdCancel />} label="Missing" sx={{ ...pill('#fee2e2', '#b91c1c'), '& .MuiChip-icon': { color: 'inherit' } }} />
        );
      default:
        if (c.money) {
          const v = r[c.key] || 0;
          return <span style={{ color: v === 0 && ['cgst', 'sgst', 'igst', 'roundOff'].includes(c.key) ? '#94a3b8' : undefined }}>{inr(v)}</span>;
        }
        return r[c.key] ?? '-';
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Invoice Report</Typography>
          <Typography sx={{ fontSize: 14, color: '#64748b', fontFamily: FONT }}>Invoice register with GST breakup for accounts and GST filing</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>
            Note
          </Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <TextField
              label="Search invoice no. / order no. / customer / phone"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField select label="Customer Type" size="small" fullWidth value={customerType} onChange={setFilter(setCustomerType)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="b2b">B2B (business)</MenuItem>
              <MenuItem value="b2c">B2C (consumer)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField select label="Place of Supply" size="small" fullWidth value={state} onChange={setFilter(setState)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All states</MenuItem>
              {indianStates.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField select label="Invoice Status" size="small" fullWidth value={invoiceStatus} onChange={setFilter(setInvoiceStatus)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="issued">Issued</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="returned">Returned (credit note due)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField select label="Payment Status" size="small" fullWidth value={paymentStatus} onChange={setFilter(setPaymentStatus)}>
              <MenuItem value="">All</MenuItem>
              {Object.keys(PAYMENT_STATUS_STYLES).map((k) => (
                <MenuItem key={k} value={k} sx={{ textTransform: 'capitalize' }}>{k}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField select label="Invoice PDF" size="small" fullWidth value={pdf} onChange={setFilter(setPdf)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="available">PDF available</MenuItem>
              <MenuItem value="missing">PDF missing</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={setFilter(setSortBy)}>
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Invoices</Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b', fontFamily: FONT }}>{total.toLocaleString('en-IN')} invoices</Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 580 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                    sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>
                    No invoices match the selected filters.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow
                  key={r.invoiceNumber || idx}
                  hover
                  sx={{
                    '&:nth-of-type(even)': { background: '#f8fafc' },
                    opacity: r.invoiceStatus === 'cancelled' ? 0.7 : 1,
                  }}
                >
                  {COLUMNS.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                      sx={{
                        fontFamily: FONT,
                        whiteSpace: 'nowrap',
                        fontSize: 13,
                        fontWeight: ['invoiceNumber', 'invoiceTotal'].includes(c.key) ? 700 : 500,
                        color: '#334155',
                        textDecoration: r.invoiceStatus === 'cancelled' && c.key === 'invoiceTotal' ? 'line-through' : 'none',
                      }}
                    >
                      {renderCell(c, r)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceReport;
